import { FilesCollection } from '@/stores/useFiles.ts'
import { AlbumFile } from '@/models/albumFile.ts'
import { AlbumFileType } from '@/models/albumFileType.ts'
import { IconCircleCheck, IconHeartFilled, IconPlayerPlay, IconVideo } from '@tabler/icons-react';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { FileSelectionType } from '@/hooks/useFileSelection.tsx'
import { useProfile } from '@/stores/useAuth.ts'
import { HeyProfile } from '@/models/profile.ts'
import { useNavigate } from 'react-router-dom'
import FileHoverOverlay, { FileAction, FileNameBar } from '@/components/Files/FileHoverOverlay.tsx'
import FileActionsModal from '@/components/Files/FileActionsModal.tsx'
import FileActionBar, { HeartDescriptor } from '@/components/Files/FileActionBar.tsx'
import { useFileActions } from '@/hooks/useFileActions.ts'
import { useIsMobile } from '@/hooks/useIsMobile.ts'
import useLongPress from '@/hooks/useLongPress.ts'
import { RowsPhotoAlbum, ColumnsPhotoAlbum, MasonryPhotoAlbum, Photo } from 'react-photo-album'

export type LayoutMode = 'grid' | 'rows' | 'columns' | 'masonry'

/**
 * The per-file heart is a real Like on normal drives, but on professional
 * drives it toggles the (existing) file selection instead — the Like endpoint
 * is never called there.
 */
export type FileActionBarMode = 'like' | 'select'

const DOUBLE_TAP_MS = 200

/**
 * Tile click handling shared by both grid renderers.
 * When double-tap/click toggling is enabled, a double interaction toggles
 * (like / selection) and a single one opens the file after a short delay (to
 * disambiguate the two). Otherwise the click opens the file immediately.
 */
function useTileClick(
  file: AlbumFile,
  onOpen: (file: AlbumFile) => void,
  onDoubleToggle: (() => void) | undefined,
  enableDoubleToggle: boolean,
  didLongPress: React.MutableRefObject<boolean>,
) {
  const lastTapRef = useRef(0)
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current)
      }
    }
  }, [])

  return function handleClick() {
    if (didLongPress.current) {
      return
    }

    if (!enableDoubleToggle || !onDoubleToggle) {
      onOpen(file)
      return
    }

    const now = Date.now()
    if (now - lastTapRef.current < DOUBLE_TAP_MS) {
      // Second tap: double interaction -> toggle, cancel the pending open.
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current)
        openTimerRef.current = null
      }
      lastTapRef.current = 0
      onDoubleToggle()
      return
    }

    // First tap: wait briefly to see whether a second tap follows.
    lastTapRef.current = now
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
    }
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null
      lastTapRef.current = 0
      onOpen(file)
    }, DOUBLE_TAP_MS)
  }
}

interface TileActionBar {
  share?: { onClick: () => void; busy?: boolean }
  download?: { onClick: () => void; busy?: boolean }
  heart?: HeartDescriptor
  showMobileHeart: boolean
  /** Show the desktop heart without hover (while a selection is active). */
  heartAlwaysVisible: boolean
  onDoubleToggle?: () => void
  canDoubleToggle: boolean
}

/**
 * Derives the FileActionBar props for a single tile from the current mode,
 * the file's like/download/share actions and the selection state.
 */
function buildTileActionBar(
  mode: FileActionBarMode,
  selectionEnabled: boolean,
  actions: ReturnType<typeof useFileActions>,
  fileSelection: FileSelectionType | undefined,
  file: AlbumFile,
  albumId: string | undefined,
): TileActionBar {
  const share = actions.canShare && !actions.hideShare ? { onClick: actions.share, busy: actions.isSharing } : undefined
  const download = !actions.hideDownload ? { onClick: actions.download, busy: actions.isDownloading } : undefined

  if (mode === 'select') {
    if (selectionEnabled && fileSelection) {
      const isSelected = fileSelection.isFileSelected(file, albumId)
      const selecting = fileSelection.isSelecting
      const toggle = () => fileSelection.toggleFileInSelection(file, albumId)
      return {
        share,
        download,
        heart: { active: isSelected, onClick: toggle, title: 'Auswählen' },
        // While a selection is active, every heart is visible (desktop + mobile)
        // for easy (de)selection. Otherwise the heart only appears on hover.
        showMobileHeart: selecting,
        heartAlwaysVisible: selecting,
        onDoubleToggle: toggle,
        canDoubleToggle: true,
      }
    }
    return { share, download, showMobileHeart: false, heartAlwaysVisible: false, canDoubleToggle: false }
  }

  // Like mode (normal drives)
  if (actions.canLike) {
    return {
      share,
      download,
      heart: { active: !!actions.yourLike, busy: actions.isLiking, onClick: actions.toggleLike, title: 'Gefällt mir' },
      // On mobile the heart only shows when the file is already liked.
      showMobileHeart: !!actions.yourLike,
      heartAlwaysVisible: false,
      onDoubleToggle: actions.toggleLike,
      canDoubleToggle: true,
    }
  }
  return { share, download, showMobileHeart: false, heartAlwaysVisible: false, canDoubleToggle: false }
}

// Extended Photo type that includes the original AlbumFile
interface PhotoWithFile extends Photo {
  albumFile: AlbumFile
}

interface FilesListingProps {
  files: FilesCollection
  onFileClick: (file: AlbumFile) => void
  prependChild?: ReactNode
  fileSelection?: FileSelectionType
  adMode?: string | null
  albumId?: string | undefined
  fileActions?: FileAction[]
  /**
   * Enables the per-file action bar (drive views only, not share/portfolio).
   * Requires a surrounding drive + files context.
   */
  showFileActionBar?: boolean
  /** Heart behaves as a Like ('like', default) or selection toggle ('select'). */
  fileActionBarMode?: FileActionBarMode
  /** Whether selection is available (gates the heart in 'select' mode). */
  selectionEnabled?: boolean
  displayMode?: 'cover' | 'contain'
  /** Permanently show the file name on every tile (instead of on hover only). */
  showFileNames?: boolean
  layoutMode?: LayoutMode
  spacing?: number
}

export default function FilesListing({ files, onFileClick, prependChild, fileSelection, adMode, albumId, fileActions, showFileActionBar, fileActionBarMode = 'like', selectionEnabled = false, displayMode = 'cover', showFileNames = false, layoutMode = 'grid', spacing = 4 }: FilesListingProps) {
  const {profile} = useProfile()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const showOverlay = !!fileActions && fileActions.length > 0 && !fileSelection?.isSelecting
  // The action bar stays visible during selection in 'select' mode (that's the
  // whole point there); in 'like' mode it hides while the classic selection UI
  // takes over the tiles.
  const showActionBar = !!showFileActionBar && (fileActionBarMode === 'select' || !fileSelection?.isSelecting)

  // Mobile long-press opens a modal instead of inline overlay
  const [longPressFile, setLongPressFile] = useState<AlbumFile | null>(null)

  function renderComponentIndex(file: AlbumFile, index: number) {
    const component = <FilesListingComponent
      key={file.id}
      file={file}
      onClick={onFileClick}
      isSelected={!!fileSelection?.isSelecting && fileSelection.isFileSelected(file, albumId)}
      profile={profile}
      fileActions={showOverlay ? fileActions : undefined}
      showActionBar={showActionBar}
      actionBarMode={fileActionBarMode}
      selectionEnabled={selectionEnabled}
      fileSelection={fileSelection}
      albumId={albumId}
      isMobile={isMobile}
      displayMode={displayMode}
      showFileName={showFileNames}
      onLongPress={showOverlay ? setLongPressFile : undefined}
    />
    if (!adMode) {
      return component
    }

    if (adMode === 'm1' && index > 0 && index % 7 == 0) {
      const toYes = () => navigate('yes')
      return <>
        <div className='cursor-pointer bg-primary text-neutral flex justify-center items-center flex-col text-center gap-2 p-1' onClick={toYes}>
          <span className='text-sm font-bold'>Ja, ich will</span>
          <p className='text-sm'>Gästebilder meiner Hochzeitsgäste sammeln</p>
          <span className='font-bold text-sm underline'>Gutschein sichern</span>
        </div>
        { component }
      </>
    }

    return component
  }

  // Convert AlbumFile to Photo for react-photo-album
  // Uses 1000x1000 as fallback if dimensions are missing
  const photos: PhotoWithFile[] = useMemo(() => {
    return files.files.map(file => ({
      src: file.thumbnail || file.detail || file.file,
      width: file.width ?? 1000,
      height: file.height ?? 1000,
      key: file.id,
      albumFile: file,
    }))
  }, [files.files])

  // Render function for react-photo-album photos
  // The render.photo function receives (props, context) where context contains photo, width, height
  const renderPhoto = (_props: unknown, context: { photo: PhotoWithFile, width: number, height: number }) => {
    const file = context.photo.albumFile
    const isSelected = !!fileSelection?.isSelecting && fileSelection.isFileSelected(file, albumId)
    return (
      <PhotoAlbumItem
        key={file.id}
        file={file}
        width={context.width}
        height={context.height}
        onClick={onFileClick}
        isSelected={isSelected}
        profile={profile}
        fileActions={showOverlay ? fileActions : undefined}
        showActionBar={showActionBar}
        actionBarMode={fileActionBarMode}
        selectionEnabled={selectionEnabled}
        fileSelection={fileSelection}
        albumId={albumId}
        isMobile={isMobile}
        showFileName={showFileNames}
        onLongPress={showOverlay ? setLongPressFile : undefined}
      />
    )
  }

  // Grid layout (original)
  if (layoutMode === 'grid') {
    return <>
      <div className='grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1'>
        { prependChild }
        { files.files.map((file, i) => renderComponentIndex(file, i)) }
      </div>
      {/* Mobile action modal (long-press) */}
      {fileActions && (
        <FileActionsModal
          file={longPressFile}
          actions={fileActions}
          onClose={() => setLongPressFile(null)}
        />
      )}
    </>
  }

  // react-photo-album layouts
  const baseAlbumProps = {
    photos,
    render: { photo: renderPhoto },
    spacing,
  }

  return <>
    {prependChild && (
      <div className='mb-2 w-24 h-24'>
        {prependChild}
      </div>
    )}
    {layoutMode === 'rows' && <RowsPhotoAlbum {...baseAlbumProps} rowConstraints={{ singleRowMaxHeight: 400 }} />}
    {layoutMode === 'columns' && <ColumnsPhotoAlbum {...baseAlbumProps} />}
    {layoutMode === 'masonry' && <MasonryPhotoAlbum {...baseAlbumProps} />}
    {/* Mobile action modal (long-press) */}
    {fileActions && (
      <FileActionsModal
        file={longPressFile}
        actions={fileActions}
        onClose={() => setLongPressFile(null)}
      />
    )}
  </>
}

function secondsToTime(e: number){
  const h = Math.floor(e / 3600).toString().padStart(2,'0'),
    m = Math.floor(e % 3600 / 60).toString().padStart(2,'0'),
    s = Math.floor(e % 60).toString().padStart(2,'0');

  if (h == '00') {
    return m + ':' + s;
  }

  return h + ':' + m + ':' + s;
}

function LikoComponent() {
  return <div className='text-primary absolute left-2 bottom-2'>
    <IconHeartFilled />
  </div>
}

/**
 * Permanently visible file name on top of a tile (opt-in via `showFileNames`).
 * Uses the same bar as the hover overlay so both look identical.
 */
function FileNameLabel({ file }: { file: AlbumFile }) {
  return <FileNameBar file={file} className='absolute top-0 left-0 right-0 pointer-events-none' />
}

interface TileRenderProps {
  file: AlbumFile
  onClick: (file: AlbumFile) => void
  isSelected: boolean
  profile: HeyProfile | null
  fileActions?: FileAction[]
  showActionBar?: boolean
  actionBarMode?: FileActionBarMode
  selectionEnabled?: boolean
  fileSelection?: FileSelectionType
  albumId?: string
  isMobile?: boolean
  /** Render the file name permanently on the tile. */
  showFileName?: boolean
  onLongPress?: (file: AlbumFile) => void
}

function FilesListingComponent({ file, onClick, isSelected, profile, fileActions, showActionBar, actionBarMode = 'like', selectionEnabled = false, fileSelection, albumId, isMobile, displayMode = 'cover', showFileName, onLongPress }: TileRenderProps & { displayMode?: 'cover' | 'contain' }) {
  const yourLike = file.likes && profile ? file.likes.find(file => file.author === profile.username) : null
  const [hovered, setHovered] = useState(false)
  const actions = useFileActions(file, albumId)
  const bar = buildTileActionBar(actionBarMode, selectionEnabled, actions, fileSelection, file, albumId)

  const longPress = useLongPress(() => {
    if (onLongPress) {
      onLongPress(file)
    }
  })

  // Double toggle: desktop + mobile in 'select' mode, mobile only in 'like' mode.
  const enableDoubleToggle = showActionBar === true && bar.canDoubleToggle && (actionBarMode === 'select' || !!isMobile)
  const handleClick = useTileClick(file, onClick, bar.onDoubleToggle, enableDoubleToggle, longPress.didLongPress)

  const containClasses = displayMode === 'contain' ? 'bg-base-300' : ''
  const bgSize = displayMode === 'contain' ? 'contain' : 'cover'

  if (file.file_type === AlbumFileType.IMAGE) {
    return <div
      className={`aspect-square bg-center cursor-pointer bg-secondary relative select-none ${containClasses}`}
      style={{ backgroundImage: `url("${file.thumbnail}")`, backgroundSize: bgSize, backgroundRepeat: 'no-repeat' }}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...longPress.handlers}
    >
      { isSelected && <SelectedFileOverlay showCheck={actionBarMode !== 'select'} /> }
      { !showActionBar && yourLike && <LikoComponent /> }
      { fileActions && <FileHoverOverlay file={file} actions={fileActions} visible={hovered} hideFileName={showFileName} /> }
      { showFileName && <FileNameLabel file={file} /> }
      { showActionBar && <FileActionBar share={bar.share} download={bar.download} heart={bar.heart} hovered={hovered} showMobileHeart={bar.showMobileHeart} alwaysShowHeart={bar.heartAlwaysVisible} /> }
    </div>
  }

  const videoDurationString = useMemo(() => {
    if (file.video_duration_seconds == null) {
      return null
    }

    return secondsToTime(file.video_duration_seconds)
  }, [file.video_duration_seconds])

  if (file.file_type === AlbumFileType.VIDEO) {
    const style: React.CSSProperties = file.thumbnail
      ? { backgroundImage: `url("${file.thumbnail}")`, backgroundSize: bgSize, backgroundRepeat: 'no-repeat' }
      : {}
    return <div
      className={`aspect-square bg-center cursor-pointer bg-secondary flex items-center justify-center text-primary relative select-none ${containClasses}`}
      style={style}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...longPress.handlers}
    >
      { !file.thumbnail && <IconVideo size={50}/> }
      { file.thumbnail && <div className='border-4 border-neutral rounded-full aspect-square p-2'>
        <IconPlayerPlay className='text-neutral' size={40} />
      </div> }
      { videoDurationString && <div className='absolute bg-[black] bg-opacity-80 py-[1px] px-[5px] rounded bottom-1 right-1 text-[0.75rem] text-[white]'>{ videoDurationString }</div> }
      { isSelected && <SelectedFileOverlay showCheck={actionBarMode !== 'select'} /> }
      { !showActionBar && yourLike && <LikoComponent /> }
      { fileActions && <FileHoverOverlay file={file} actions={fileActions} visible={hovered} hideFileName={showFileName} /> }
      { showFileName && <FileNameLabel file={file} /> }
      { showActionBar && <FileActionBar share={bar.share} download={bar.download} heart={bar.heart} hovered={hovered} showMobileHeart={bar.showMobileHeart} alwaysShowHeart={bar.heartAlwaysVisible} /> }
    </div>
  }
}

function SelectedFileOverlay({ showCheck = true }: { showCheck?: boolean }) {
  return <div className='absolute w-full h-full top-0 left-0 bg-[black] bg-opacity-30 flex items-end justify-end p-1'>
    {showCheck && <IconCircleCheck className='bg-primary rounded-full text-neutral' size={24} />}
  </div>
}

// Component for react-photo-album layouts
function PhotoAlbumItem({ file, width, height, onClick, isSelected, profile, fileActions, showActionBar, actionBarMode = 'like', selectionEnabled = false, fileSelection, albumId, isMobile, showFileName, onLongPress }: TileRenderProps & {
  width: number
  height: number
}) {
  const yourLike = file.likes && profile ? file.likes.find(f => f.author === profile.username) : null
  const [hovered, setHovered] = useState(false)
  const actions = useFileActions(file, albumId)
  const bar = buildTileActionBar(actionBarMode, selectionEnabled, actions, fileSelection, file, albumId)

  const longPress = useLongPress(() => {
    if (onLongPress) {
      onLongPress(file)
    }
  })

  const enableDoubleToggle = showActionBar === true && bar.canDoubleToggle && (actionBarMode === 'select' || !!isMobile)
  const handleClick = useTileClick(file, onClick, bar.onDoubleToggle, enableDoubleToggle, longPress.didLongPress)

  const isVideo = file.file_type === AlbumFileType.VIDEO

  const videoDurationString = useMemo(() => {
    if (file.video_duration_seconds == null) {
      return null
    }
    return secondsToTime(file.video_duration_seconds)
  }, [file.video_duration_seconds])

  const imgSrc = file.thumbnail || file.detail || file.file

  return (
    <div
      className='cursor-pointer bg-secondary relative overflow-hidden select-none'
      style={{ width, height }}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...longPress.handlers}
    >
      <img
        src={imgSrc}
        alt=''
        className='w-full h-full object-cover select-none'
        draggable={false}
        loading='lazy'
        decoding='async'
      />
      {isVideo && (
        <>
          <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
            <div className='border-4 border-neutral rounded-full aspect-square p-2'>
              <IconPlayerPlay className='text-neutral' size={Math.min(40, width / 4)} />
            </div>
          </div>
          {videoDurationString && (
            <div className='absolute bg-[black] bg-opacity-80 py-[1px] px-[5px] rounded bottom-1 right-1 text-[0.75rem] text-[white]'>
              {videoDurationString}
            </div>
          )}
        </>
      )}
      {isSelected && <SelectedFileOverlay showCheck={actionBarMode !== 'select'} />}
      {!showActionBar && yourLike && <LikoComponent />}
      {fileActions && <FileHoverOverlay file={file} actions={fileActions} visible={hovered} hideFileName={showFileName} />}
      {showFileName && <FileNameLabel file={file} />}
      {showActionBar && <FileActionBar share={bar.share} download={bar.download} heart={bar.heart} hovered={hovered} showMobileHeart={bar.showMobileHeart} alwaysShowHeart={bar.heartAlwaysVisible} />}
    </div>
  )
}
